import { useLazyQuery } from "@apollo/client";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import { Box, Button, Stack } from "@mui/material";
import { gql } from "generated-graphql";
import { useTenantId } from "hooks/useTenant";
import { MouseEvent, useState } from "react";

import type { SandboxExportDownload } from "./toolRenderers";

/**
 * Fetched per click, never cached: each click deserves a freshly minted URL,
 * which is the whole reason the transcript stores a key instead of a
 * presigned URL (history links that never go stale, and no signed URL for
 * the model to mistype).
 */
export const SCOUT_SANDBOX_ARTIFACT_DOWNLOAD = gql(`
  query ScoutSandboxArtifactDownload(
    $conversationId: ID!
    $artifactKey: String!
    $filename: String
  ) {
    scoutSandboxArtifactDownload(
      conversationId: $conversationId
      artifactKey: $artifactKey
      filename: $filename
    ) {
      url
      filename
    }
  }
`);

/**
 * Per-file downloads read as plain text; "Download all" is outlined so it
 * carries the visual weight of the primary action. Text buttons keep a
 * button's hit area and hover state, so the rows stay comfortable targets
 * without a row of boxes competing with the message for attention.
 */
const PER_FILE_VARIANT = "text" as const;
const DOWNLOAD_ALL_VARIANT = "outlined" as const;

type DownloadVariant = typeof PER_FILE_VARIANT | typeof DOWNLOAD_ALL_VARIANT;

/**
 * Same-tab navigation to a `Content-Disposition: attachment` URL downloads
 * the file without unloading the SPA. An anchor click (rather than
 * `window.open`) stays inside the user's click gesture semantics and is
 * spy-able in jsdom, where `location.assign` is not implemented.
 */
function navigateToDownload(url: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  anchor.click();
}

/**
 * Mints fresh URLs for the given artifacts and starts their downloads.
 *
 * Shared by the per-file and download-all buttons so the two cannot drift on
 * how a URL is obtained. URLs are minted concurrently but the downloads are
 * started in sequence, because a browser drops all but the last navigation
 * when several fire in the same tick.
 */
function useMintAndDownload() {
  const tenantId = useTenantId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [fetchDownload] = useLazyQuery(SCOUT_SANDBOX_ARTIFACT_DOWNLOAD, {
    fetchPolicy: "no-cache",
  });

  const start = (downloads: SandboxExportDownload[]) => {
    if (!tenantId || busy) {
      return;
    }
    setBusy(true);
    setFailed(false);
    void Promise.all(
      downloads.map(async (download) => {
        const result = await fetchDownload({
          variables: {
            conversationId: download.artifactRef.conversationId,
            artifactKey: download.artifactRef.artifactKey,
            // Carries spaces and non-ASCII that the storage key had to strip,
            // so the file saves under the name the user was shown.
            filename: download.filename,
          },
        });
        return result.data?.scoutSandboxArtifactDownload.url ?? null;
      })
    )
      .then((urls) => {
        const resolved = urls.filter((url): url is string => url !== null);
        if (resolved.length === 0) {
          setFailed(true);
          return;
        }
        // Any artifact that failed to mint is reported even when others
        // succeeded, so a partial download-all doesn't look complete.
        if (resolved.length < urls.length) {
          setFailed(true);
        }
        for (const url of resolved) {
          navigateToDownload(url);
        }
      })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  };

  return { tenantId, busy, failed, start };
}

/**
 * A download button whose width does not change when its label does.
 *
 * "Preparing…" is shorter than the resting label, so swapping the text
 * outright would shrink the button mid-click. The resting label stays in the
 * DOM (hidden, retaining its box) to hold the width, and the transient label
 * is centered over it. Measuring the real string rather than guessing a
 * min-width keeps this correct for any filename.
 */
function DownloadButton({
  restingLabel,
  transientLabel,
  variant,
  onClick,
}: {
  restingLabel: string;
  transientLabel: string | null;
  variant: DownloadVariant;
  onClick: (event: MouseEvent<HTMLElement>) => void;
}) {
  return (
    <Button
      type="button"
      variant={variant}
      size="small"
      disableElevation
      onClick={onClick}
      startIcon={<FileDownloadOutlined />}
      // Both labels sit in the DOM while a transient one shows, so the name is
      // set explicitly rather than concatenated from them.
      aria-label={transientLabel ?? restingLabel}
      sx={{
        textTransform: "none",
        // The enclosing trace row sets a small, muted type scale for tool
        // output; a deliverable is the one thing in that row the user is
        // meant to act on, so it opts back out to normal body sizing.
        fontSize: (theme) => theme.typography.body2.fontSize,
        // MUI pads a small text button 4px/5px and a small outlined one
        // 3px/9px plus a 1px border — so left edges would sit 5px apart in a
        // stack. Matching the outlined content offset (9 + 1) keeps the icons
        // in one column; paying it as padding rather than margin keeps the
        // whole row hoverable, which is the point of using a button here.
        ...(variant === "text" ? { px: "10px" } : {}),
      }}
    >
      <Box component="span" sx={{ display: "grid" }}>
        <Box
          component="span"
          aria-hidden={transientLabel !== null}
          sx={{ gridArea: "1 / 1", visibility: transientLabel ? "hidden" : undefined }}
        >
          {restingLabel}
        </Box>
        {transientLabel !== null && (
          <Box
            component="span"
            sx={{ gridArea: "1 / 1", justifySelf: "center", whiteSpace: "nowrap" }}
          >
            {transientLabel}
          </Box>
        )}
      </Box>
    </Button>
  );
}

export interface ScoutSandboxArtifactDownloadsProps {
  downloads: SandboxExportDownload[];
}

/**
 * Download affordances for the sandbox exports in one assistant turn.
 *
 * One row per file, plus a "Download all" when a turn produced several —
 * Scout often writes a set of CSVs rather than one archive, and clicking each
 * in turn is the tedious case this removes. The per-file rows are text and the
 * all-action is outlined, so the group reads as a list with one primary action
 * rather than a wall of equally-weighted buttons.
 */
export function ScoutSandboxArtifactDownloads({ downloads }: ScoutSandboxArtifactDownloadsProps) {
  const { tenantId, busy, failed, start } = useMintAndDownload();

  if (!tenantId || downloads.length === 0) {
    return null;
  }

  const onClickAll = (event: MouseEvent<HTMLElement>) => {
    // The enclosing ToolCallGroup header toggles expansion on click; these
    // clicks are for downloads only.
    event.stopPropagation();
    event.preventDefault();
    start(downloads);
  };

  let allTransientLabel: string | null = null;
  if (busy) {
    allTransientLabel = "Preparing…";
  } else if (failed) {
    allTransientLabel = "Download failed — try again";
  }

  return (
    <Stack alignItems="flex-start" sx={{ mt: 1.5 }}>
      {/*
        The file links sit closer together than they do to "Download all", so
        they read as one list rather than as separate actions — but not flush,
        which ran their hover areas together.
      */}
      <Stack spacing={0.5} alignItems="flex-start">
        {downloads.map((download) => (
          <ScoutSandboxArtifactDownload key={download.key} download={download} />
        ))}
      </Stack>
      {downloads.length > 1 && (
        // Set off from the tight list above so the primary action reads as
        // separate from it rather than as one more row.
        <Box sx={{ mt: 1.5 }}>
          <DownloadButton
            restingLabel={`Download all ${String(downloads.length)} files`}
            transientLabel={allTransientLabel}
            variant={DOWNLOAD_ALL_VARIANT}
            onClick={onClickAll}
          />
        </Box>
      )}
    </Stack>
  );
}

export interface ScoutSandboxArtifactDownloadProps {
  download: SandboxExportDownload;
}

/**
 * The download affordance for a single `sandbox_export_file` result. Clicking
 * asks the API to mint a fresh short-lived URL and then starts the download.
 */
export function ScoutSandboxArtifactDownload({ download }: ScoutSandboxArtifactDownloadProps) {
  const { tenantId, busy, failed, start } = useMintAndDownload();

  if (!tenantId) {
    return null;
  }

  const onClick = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    start([download]);
  };

  // The filename carries the label: "Download facilities.csv" tells the user
  // what they get without having to read the trace row above it.
  const restingLabel = download.filename ? `Download ${download.filename}` : "Download";
  let transientLabel: string | null = null;
  if (busy) {
    transientLabel = "Preparing…";
  } else if (failed) {
    // Longer than the resting label, so it sizes the button itself rather than
    // being held to a width it would overflow.
    transientLabel = "Download failed — try again";
  }

  return (
    <DownloadButton
      restingLabel={restingLabel}
      transientLabel={transientLabel}
      variant={PER_FILE_VARIANT}
      onClick={onClick}
    />
  );
}
